import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';

const COMMUNITY_ROOMS = [
  {
    id: 'all',
    label: 'All Rooms',
    icon: 'Globe',
    memberCount: null,
  },
  {
    id: 'general',
    label: 'General',
    icon: 'MessageCircle',
    memberCount: 142,
  },
  {
    id: 'investing-101',
    label: 'Investing 101',
    icon: 'BookOpen',
    memberCount: 89,
  },
  {
    id: 'portfolio-review',
    label: 'Portfolio Review',
    icon: 'PieChart',
    memberCount: 56,
  },
  {
    id: 'entrepreneurs',
    label: 'Entrepreneurs',
    icon: 'Rocket',
    memberCount: 73,
  },
  {
    id: 'market-news',
    label: 'Market News',
    icon: 'Newspaper',
    memberCount: 201,
  },
];

export default function CommunityRoomSelector({
  activeRoom = 'all',
  onRoomChange,
  className = '',
  isMobile = false,
}) {
  const [currentRoom, setCurrentRoom] = useState(activeRoom);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleRoomSelect = (roomId) => {
    setCurrentRoom(roomId);
    setDropdownOpen(false);
    if (onRoomChange) {
      onRoomChange(roomId);
    }
  };

  const activeRoomData = COMMUNITY_ROOMS?.find(r => r?.id === currentRoom) || COMMUNITY_ROOMS?.[0];

  // Mobile: dropdown selector
  if (isMobile) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          className="flex items-center gap-2 w-full px-4 py-3 rounded-md border font-caption font-medium text-sm transition-all duration-250"
          style={{
            background: 'var(--color-background)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-foreground)',
          }}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
          aria-label="Select community room"
        >
          <Icon name={activeRoomData?.icon} size={16} color="var(--color-accent)" strokeWidth={1.75} />
          <span className="flex-1 text-left">{activeRoomData?.label}</span>
          {activeRoomData?.memberCount && (
            <span
              className="font-caption text-muted-foreground"
              style={{ fontSize: '0.75rem' }}
            >
              {activeRoomData?.memberCount} online
            </span>
          )}
          <Icon
            name={dropdownOpen ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            color="var(--color-muted-foreground)"
          />
        </button>
        {dropdownOpen && (
          <div
            className="absolute top-full left-0 right-0 mt-1 rounded-md shadow-lg overflow-hidden"
            style={{
              background: 'var(--color-primary)',
              zIndex: 'var(--z-dropdown)',
              border: '1px solid rgba(212, 175, 55, 0.2)',
            }}
            role="listbox"
            aria-label="Community rooms"
          >
            {COMMUNITY_ROOMS?.map((room) => (
              <button
                key={room?.id}
                role="option"
                aria-selected={currentRoom === room?.id}
                className="flex items-center gap-3 w-full px-4 py-3 text-left transition-all duration-250"
                style={{
                  color: currentRoom === room?.id ? 'var(--color-accent)' : 'rgba(255,255,255,0.8)',
                  background: currentRoom === room?.id ? 'rgba(212, 175, 55, 0.12)' : 'transparent',
                  fontSize: '0.875rem',
                  fontFamily: 'var(--font-caption)',
                }}
                onClick={() => handleRoomSelect(room?.id)}
                onMouseEnter={e => {
                  if (currentRoom !== room?.id) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                }}
                onMouseLeave={e => {
                  if (currentRoom !== room?.id) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon
                  name={room?.icon}
                  size={16}
                  color={currentRoom === room?.id ? 'var(--color-accent)' : 'currentColor'}
                  strokeWidth={1.75}
                />
                <span className="flex-1">{room?.label}</span>
                {room?.memberCount && (
                  <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                    {room?.memberCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop: horizontal pill selector
  return (
    <div
      className={`community-room-selector ${className}`}
      role="tablist"
      aria-label="Community rooms"
    >
      {COMMUNITY_ROOMS?.map((room) => {
        const isActive = currentRoom === room?.id;
        return (
          <button
            key={room?.id}
            role="tab"
            aria-selected={isActive}
            className={`community-room-btn ${isActive ? 'active' : ''}`}
            onClick={() => handleRoomSelect(room?.id)}
          >
            <Icon
              name={room?.icon}
              size={15}
              color={isActive ? '#FFFFFF' : 'currentColor'}
              strokeWidth={1.75}
            />
            <span>{room?.label}</span>
            {room?.memberCount && (
              <span
                className="inline-flex items-center justify-center rounded-full"
                style={{
                  fontSize: '0.6875rem',
                  fontFamily: 'var(--font-caption)',
                  fontWeight: 500,
                  minWidth: '20px',
                  height: '18px',
                  padding: '0 5px',
                  background: isActive ? 'rgba(255,255,255,0.2)' : 'var(--color-muted)',
                  color: isActive ? '#FFFFFF' : 'var(--color-muted-foreground)',
                }}
              >
                {room?.memberCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}